<script>
  import { Pause, Play, Volume2 } from 'lucide-svelte';
  import { onDestroy, onMount } from 'svelte';
  import { normalizeUrl } from '$lib/utils';

  export let payload;
  export let isMine = false;
  export let compact = false;

  const PLAY_EVENT = 'k2:voice-message-play';
  const DEFAULT_WAVEFORM = [
    0.22, 0.4, 0.68, 0.35, 0.82, 0.48, 0.65, 0.34, 0.74, 0.42, 0.9, 0.52,
    0.71, 0.3, 0.62, 0.44, 0.8, 0.36, 0.68, 0.5, 0.88, 0.46, 0.72, 0.32,
    0.58, 0.4, 0.76, 0.52, 0.84, 0.38, 0.66, 0.48, 0.7, 0.34, 0.56, 0.25
  ];

  let audio;
  let playing = false;
  let currentTime = 0;
  let mediaDuration = 0;
  let playbackRate = 1;
  let loadError = false;
  const instanceId = `voice-${Math.random().toString(36).slice(2)}`;

  function parsePayload(value) {
    let parsed = value;
    if (typeof value === 'string') {
      try {
        parsed = JSON.parse(value);
      } catch {
        parsed = { url: value };
      }
    }

    if (!parsed || typeof parsed !== 'object') parsed = {};
    const waveform = Array.isArray(parsed.waveform)
      ? parsed.waveform.slice(0, 64).map((value) => Math.max(0.1, Math.min(1, Number(value) || 0.1)))
      : DEFAULT_WAVEFORM;

    return {
      url: normalizeUrl(parsed.url || parsed.src || ''),
      duration: Math.max(0, Math.min(300, Number(parsed.duration) || 0)),
      mime: String(parsed.mime || ''),
      waveform: waveform.length >= 8 ? waveform : DEFAULT_WAVEFORM
    };
  }

  function formatDuration(value = 0) {
    const seconds = Math.max(0, Math.floor(value));
    return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  }

  async function togglePlayback() {
    if (!audio || loadError) return;
    if (audio.paused) {
      window.dispatchEvent(new CustomEvent(PLAY_EVENT, { detail: { id: instanceId } }));
      try {
        await audio.play();
      } catch {
        loadError = true;
      }
    } else {
      audio.pause();
    }
  }

  function seek(event) {
    if (!audio || !duration) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * duration;
    currentTime = audio.currentTime;
  }

  function cyclePlaybackRate() {
    playbackRate = playbackRate === 1 ? 1.5 : playbackRate === 1.5 ? 2 : 1;
    if (audio) audio.playbackRate = playbackRate;
  }

  function pauseOther(event) {
    if (event.detail?.id !== instanceId && audio && !audio.paused) audio.pause();
  }

  $: data = parsePayload(payload);
  $: duration = mediaDuration || data.duration;
  $: progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;
  $: shownTime = playing && currentTime > 0 ? currentTime : duration;

  onMount(() => {
    window.addEventListener(PLAY_EVENT, pauseOther);
  });

  onDestroy(() => {
    window.removeEventListener(PLAY_EVENT, pauseOther);
    audio?.pause();
  });
</script>

<div class:is-mine={isMine} class:compact class:error={loadError} class="voice-message">
  <button class="play-button" type="button" aria-label={playing ? 'Tạm dừng tin nhắn thoại' : 'Phát tin nhắn thoại'} on:click={togglePlayback}>
    {#if playing}
      <Pause size={compact ? 17 : 20} fill="currentColor" />
    {:else}
      <Play size={compact ? 17 : 20} fill="currentColor" />
    {/if}
  </button>

  <div class="voice-body">
    <button class="waveform" type="button" aria-label="Tua tin nhắn thoại" on:click={seek}>
      {#each data.waveform as level, index}
        <i class:played={index / Math.max(1, data.waveform.length - 1) <= progress} style={`height: ${Math.max(12, level * 100)}%`}></i>
      {/each}
    </button>
    <div class="voice-meta">
      <span>{loadError ? 'Không thể phát bản ghi' : formatDuration(shownTime)}</span>
      <span class="audio-label"><Volume2 size="12" /> Tin nhắn thoại</span>
    </div>
  </div>

  <button class="rate-button" type="button" aria-label="Đổi tốc độ phát" title="Tốc độ phát" on:click={cyclePlaybackRate}>
    {playbackRate}x
  </button>

  <audio
    bind:this={audio}
    src={data.url}
    preload="metadata"
    on:loadedmetadata={() => {
      if (Number.isFinite(audio?.duration)) mediaDuration = audio.duration;
      if (audio) audio.playbackRate = playbackRate;
    }}
    on:timeupdate={() => (currentTime = audio?.currentTime || 0)}
    on:play={() => (playing = true)}
    on:pause={() => (playing = false)}
    on:ended={() => {
      playing = false;
      currentTime = 0;
      if (audio) audio.currentTime = 0;
    }}
    on:error={() => (loadError = true)}>
  </audio>
</div>

<style>
  .voice-message {
    display: flex;
    align-items: center;
    gap: 0.62rem;
    width: 100%;
    max-width: 22rem;
    min-width: 0;
    box-sizing: border-box;
    padding: 0.25rem 0;
    color: #2563eb;
  }

  .voice-message.compact {
    max-width: 17rem;
  }

  .play-button {
    display: grid;
    place-items: center;
    width: 2.7rem;
    height: 2.7rem;
    flex: 0 0 auto;
    padding-left: 0.12rem;
    border-radius: 999px;
    color: white;
    background: linear-gradient(145deg, #1687ff, #0868e8);
    box-shadow: 0 8px 20px rgba(8, 104, 232, 0.24);
    transition: transform 120ms ease, box-shadow 120ms ease;
  }

  .play-button:active,
  .rate-button:active {
    transform: scale(0.94);
  }

  .compact .play-button {
    width: 2.25rem;
    height: 2.25rem;
  }

  .voice-body {
    display: grid;
    flex: 1;
    gap: 0.25rem;
    min-width: 0;
  }

  .waveform {
    display: flex;
    align-items: center;
    gap: 2px;
    width: 100%;
    height: 2rem;
    cursor: pointer;
  }

  .waveform i {
    width: 3px;
    min-height: 3px;
    flex: 1 1 3px;
    border-radius: 999px;
    background: rgba(59, 130, 246, 0.28);
    transition: background 100ms linear;
  }

  .waveform i.played {
    background: #1687ff;
  }

  .voice-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    color: #64748b;
    font-variant-numeric: tabular-nums;
    font-size: 0.66rem;
    line-height: 1;
  }

  .audio-label {
    display: inline-flex;
    align-items: center;
    gap: 0.18rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .rate-button {
    min-width: 2rem;
    height: 1.55rem;
    flex: 0 0 auto;
    margin-left: auto;
    border-radius: 999px;
    color: #2563eb;
    background: rgba(219, 234, 254, 0.9);
    font-size: 0.62rem;
    font-weight: 800;
    transition: transform 120ms ease;
  }

  audio { display: none; }
  .error { color: #dc2626; }
  .error .play-button { background: #94a3b8; box-shadow: none; }

  @media (max-width: 640px) {
    .voice-message {
      width: min(19rem, 72vw);
      min-width: min(14rem, 72vw);
    }

    .waveform { gap: 1px; }
    .waveform i { width: 2px; }
    .audio-label { display: none; }
  }
</style>
